import { minPlanFor, MOST_CHOSEN_PLAN, wholeDollars } from '@dale/plans';

import { BillingCycleToggle } from '../../components/BillingCycleToggle';
import { MapPicker } from '../../components/MapPicker';
import { ANNUAL_DISCOUNT_PCT, PLAN_LIST_PRICE_CENTS, PLAN_PITCH, planLabel, type PlanTier } from '../../lib/plan';
import { planPriceLines } from '../../lib/planPrice';
import { hintFor } from './copy';
import { chosenPlan, needsPaidPlan, planChoicesFor, reservationsOnly } from './draft';
import { Named, Question, type StepProps } from './Question';

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export function PlaceStep({ draft, update, next, error, attempt }: StepProps) {
  return (
    <Question
      id="lugar"
      title={
        draft.mode === 'independiente' ? (
          '¿Dónde atiendes?'
        ) : (
          <>
            ¿Dónde queda <Named>{draft.businessName.trim()}</Named>?
          </>
        )
      }
      hint={hintFor(draft.mode, 'lugar')}
      error={error}
      attempt={attempt}
      onSubmit={next}
    >
      <input
        className="wiz-input"
        aria-labelledby="lugar-title"
        autoFocus
        autoComplete="street-address"
        placeholder="Calle, edificio, local"
        value={draft.address}
        onChange={(event) => update({ address: event.target.value })}
      />
      <div className="wiz-map">
        <MapPicker
          lat={draft.pin?.lat ?? null}
          lng={draft.pin?.lng ?? null}
          neighborhood={draft.neighborhood ?? ''}
          country={draft.country}
          onChange={(lat, lng) => update({ pin: { lat, lng } })}
          onClear={() => update({ pin: null })}
          onAddress={(address) => update({ address })}
        />
      </div>
    </Question>
  );
}

export function HoursStep({ draft, update, next, error, attempt }: StepProps) {
  function patchDay(index: number, patch: Partial<(typeof draft.hours)[number]>) {
    update({ hours: draft.hours.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)) });
  }
  return (
    <Question id="horario" title="¿Este es tu horario?" hint={hintFor(draft.mode, 'horario')} error={error} attempt={attempt} onSubmit={next}>
      <ul className="wiz-hours">
        {draft.hours.map((hour, index) => (
          <li key={hour.dayOfWeek} data-closed={hour.isClosed ? 'true' : undefined}>
            <span className="wiz-hours-day">{DAYS[hour.dayOfWeek]}</span>
            <input
              type="time"
              step={3600}
              aria-label={`${DAYS[hour.dayOfWeek]} abre`}
              value={hour.opensAt}
              disabled={hour.isClosed}
              onChange={(event) => patchDay(index, { opensAt: event.target.value })}
            />
            <input
              type="time"
              step={3600}
              aria-label={`${DAYS[hour.dayOfWeek]} cierra`}
              value={hour.closesAt}
              disabled={hour.isClosed}
              onChange={(event) => patchDay(index, { closesAt: event.target.value })}
            />
            <label className="wiz-hours-closed">
              <input type="checkbox" checked={hour.isClosed} onChange={(event) => patchDay(index, { isClosed: event.target.checked })} />
              Cerrado
            </label>
          </li>
        ))}
      </ul>
    </Question>
  );
}

type PlanStepProps = StepProps & { submitLabel: string; busy: boolean };

/** The last question: which plan, and the button that creates the business. */
export function PlanStep(props: PlanStepProps) {
  return reservationsOnly(props.draft) ? <ReservationsStep {...props} /> : <BusinessPlanStep {...props} />;
}

/** What a reservations-only account brings, said before it exists. */
export const RESERVATIONS_INCLUDE = [
  'Tu ficha en la app de Dale, con tus fotos y tus servicios',
  'Reservas que te llegan confirmadas',
  'Calificaciones de quienes ya fueron',
] as const;

/** After the account: photos and services, and the business shows in the app. */
const RESERVATIONS_HINT = 'Sin tarjeta. Después subes tus fotos y tus servicios, y sales en la app.';

/**
 * Reservations only (Martín, 1 Nov 2026), for an independent and for a business
 * that picked no paid plan: say what it costs and brings, then create the account.
 * Classes never land here: they pick a paid plan (Martín, 2 Nov 2026).
 */
function ReservationsStep({ draft, next, error, attempt, submitLabel, busy }: PlanStepProps) {
  const prices = planPriceLines('reservas', 'monthly');
  const solo = draft.mode === 'independiente';
  return (
    <Question
      id="plan"
      title={solo ? 'Tu plan: Independientes' : 'Listo para recibir reservas'}
      hint={solo ? hintFor(draft.mode, 'plan') : RESERVATIONS_HINT}
      error={error}
      attempt={attempt}
      onSubmit={next}
      submitLabel={submitLabel}
      busy={busy}
    >
      <div className="wiz-plan wiz-plan-solo">
        <span className="wiz-plan-price">{prices.price}</span>
        <ul className="wiz-plan-gets">
          {RESERVATIONS_INCLUDE.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </Question>
  );
}

/**
 * The paid plans: a business that came with one in the link, and every class studio, gym or
 * training center (independents who teach or train too), which start on Base.
 */
function BusinessPlanStep({ draft, update, next, error, attempt, submitLabel, busy }: PlanStepProps) {
  const chosen = chosenPlan(draft);
  const base = minPlanFor('clases');
  return (
    <Question
      id="plan"
      title={
        draft.mode === 'independiente' ? (
          '¿Con qué plan empiezas?'
        ) : (
          <>
            ¿Con qué plan empieza <Named>{draft.businessName.trim()}</Named>?
          </>
        )
      }
      hint={hintFor(draft.mode, 'plan')}
      error={error}
      attempt={attempt}
      onSubmit={next}
      submitLabel={submitLabel}
      busy={busy}
    >
      {needsPaidPlan(draft.category) ? (
        <p className="wiz-plan-nudge" role="note">
          Las clases con cupo, los planes y los paquetes empiezan en {planLabel(base)}, a {wholeDollars(PLAN_LIST_PRICE_CENTS[base])} al mes.
        </p>
      ) : null}
      <BillingCycleToggle value={draft.cycle} onChange={(cycle) => update({ cycle })} discountPct={ANNUAL_DISCOUNT_PCT} savings="bubble" />
      <div className="wiz-plans" role="radiogroup" aria-labelledby="plan-title">
        {planChoicesFor('negocio').map((plan, index) => (
          <PlanOption
            key={plan}
            plan={plan}
            index={index}
            checked={chosen === plan}
            prices={planPriceLines(plan, draft.cycle)}
            onChoose={() => update({ plan })}
          />
        ))}
      </div>
    </Question>
  );
}

type PlanOptionProps = { plan: PlanTier; index: number; checked: boolean; prices: { price: string; total?: string }; onChoose: () => void };

function PlanOption({ plan, index, checked, prices, onChoose }: PlanOptionProps) {
  return (
    <button type="button" role="radio" aria-checked={checked} className="wiz-plan" style={{ ['--i' as string]: index }} onClick={onChoose}>
      <span className="wiz-plan-head">
        <span className="wiz-plan-name">{planLabel(plan)}</span>
        {plan === MOST_CHOSEN_PLAN ? <span className="wiz-plan-note">El más elegido</span> : null}
      </span>
      <span className="wiz-plan-price">{prices.price}</span>
      {prices.total ? <span className="wiz-plan-total">{prices.total}</span> : null}
      <span className="wiz-plan-pitch">{PLAN_PITCH[plan]}</span>
    </button>
  );
}
